import { useEffect, useMemo, useState } from "react";
import { useGetIdentity, useNotify } from "ra-core";
import { Building2, Loader2, Sparkles, UserPlus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { sendClaudeRequest } from "../claude/ClaudeChat";
import type { EmailRecord } from "./EmailList";

// LDF: Kontakt (und ggf. Firma) aus einer E-Mail anlegen. Signatur wird sofort per Regeln ausgelesen,
// auf Wunsch genauer über den Claude-Worker. Nichts wird ohne Klick auf „Anlegen“ gespeichert.

const OWN = /(lovelydayflowers\.de|lovelyday\.berlin|kamkat1976@icloud\.com|kamkat@web\.de|alexanderdau@icloud\.com)$/i;
const FREEMAIL = /@(gmail|googlemail|gmx|web|t-online|icloud|me|mac|yahoo|outlook|hotmail|live|aol|freenet|posteo|mailbox|proton|protonmail|arcor|online|mail)\./i;
const LEGAL = /\b(GmbH|AG|UG|LLP|Ltd\.?|Limited|KG|OHG|e\.\s?V\.|SE|Inc\.?|S\.?A\.?|B\.?V\.?)\b/;

export type ContactDraft = {
  first_name: string;
  last_name: string;
  title: string;
  email: string;
  phone: string;
  company_name: string;
  website: string;
  address: string;
  zipcode: string;
  city: string;
  company_phone: string;
  legal: string;
};

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const counterpartOf = (email: EmailRecord) => {
  const list = email.direction === "in" ? [email.from_address] : [...(email.to_addresses ?? []), ...(email.cc_addresses ?? [])];
  return (list.find((a) => a && !OWN.test(a)) ?? "").toLowerCase();
};

const splitName = (full: string) => {
  const clean = full.replace(/\[.*?\]|["']/g, "").trim();
  if (clean.includes(",")) {
    const [last, first] = clean.split(",").map((x) => x.trim());
    return { first_name: first ?? "", last_name: last ?? "" };
  }
  const parts = clean.split(/\s+/);
  return { first_name: parts.slice(0, -1).join(" ") || parts[0] || "", last_name: parts.length > 1 ? parts.at(-1)! : "" };
};

// Regelbasiertes Auslesen der Signatur
export const parseSignature = (email: EmailRecord): ContactDraft => {
  const address = counterpartOf(email);
  const body = email.body_text ?? "";
  const draft: ContactDraft = {
    first_name: "", last_name: "", title: "", email: address, phone: "", company_name: "",
    website: "", address: "", zipcode: "", city: "", company_phone: "", legal: "",
  };

  // Name: Absendername oder „Name <adresse>“ im zitierten Verlauf
  let fullName = email.direction === "in" && email.from_address?.toLowerCase() === address ? email.from_name ?? "" : "";
  if (!fullName && address) {
    const m = body.match(new RegExp(`([A-ZÄÖÜ][\\p{L}.'-]+(?:,?\\s+[A-ZÄÖÜ][\\p{L}.'-]+)+)\\s*(?:\\[[^\\]]*\\])?\\s*<\\s*${escapeRe(address)}`, "iu"));
    if (m) fullName = m[1].replace(/^(schrieb|wrote|von|from)\s+/i, "");
  }
  if (!fullName && address) {
    fullName = address.split("@")[0].replace(/[._-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }
  Object.assign(draft, splitName(fullName));

  // Signaturblock finden: Zeile mit der Adresse als Signaturangabe („E nina@…“, „Email: …“) oder allein stehender Name
  const lines = body.split(/\r?\n/).map((l) => l.replace(/^[>\s]+/, "").trim());
  const GREETING = /^(liebe|lieber|hallo|hi|hey|dear|guten|lg|vg|mfg|beste|best|viele|kind|regards|danke|thanks)\b/i;
  const isNameLine = (l: string) =>
    !!draft.last_name && l.length < 60 && !l.includes("<") && !/schrieb|wrote|@/i.test(l) && !GREETING.test(l) &&
    l.toLowerCase().includes(draft.last_name.toLowerCase()) && (!draft.first_name || l.toLowerCase().includes(draft.first_name.split(" ")[0].toLowerCase()));
  const addrLine = address
    ? lines.findIndex((l) => l.toLowerCase().includes(address) && !l.includes("<") && !/schrieb|wrote|^(von|from|an|to|cc|gesendet|sent)\b/i.test(l))
    : -1;
  let nameLine = -1;
  if (addrLine >= 0) {
    for (let i = addrLine; i >= Math.max(0, addrLine - 10); i--) if (isNameLine(lines[i])) { nameLine = i; break; }
  }
  if (nameLine < 0) nameLine = lines.findIndex(isNameLine);
  const start = nameLine >= 0 ? nameLine + 1 : addrLine >= 0 ? Math.max(0, addrLine - 6) : lines.length;
  const block = lines.slice(start, start + 18).filter(Boolean);
  const text = block.join("\n");

  const titleLine = nameLine >= 0 ? block[0] : "";
  if (titleLine && titleLine.length < 60 && !/[@\d]|www\.|^[-_–]+$|^(E|T|M|W|Tel|Mobil|Phone|Email)\b/i.test(titleLine) && !LEGAL.test(titleLine) && !GREETING.test(titleLine)) {
    draft.title = titleLine === titleLine.toUpperCase() ? titleLine.charAt(0) + titleLine.slice(1).toLowerCase() : titleLine;
  }

  const phone = text.match(/(?:^|\s)(?:M|T|Tel|Telefon|Phone|Mobil|Mobile|Handy)\.?\s*[:.]?\s*(\+?\(?\d[\d\s()/.-]{6,}\d)/im) ?? text.match(/(\+\d{2}[\d\s()/.-]{7,}\d)/);
  if (phone) draft.phone = phone[1].replace(/\s+/g, " ").trim();

  const company = block.find((l) => LEGAL.test(l) && l.length < 80 && !/registered|registriert|amtsgericht|vertreten/i.test(l));
  if (company) draft.company_name = company.replace(/[,;].*$/, "").trim();

  const street = text.match(/([A-ZÄÖÜ][\p{L}ß.\- ]+(?:str\.?|straße|strasse|weg|platz|allee|damm|ufer|chaussee|ring|gasse)\s*\d+\s?[a-zA-Z]?)\s*,?\s*\n?\s*(\d{5})\s+([\p{L} .-]+)/iu);
  if (street) {
    draft.address = street[1].trim();
    draft.zipcode = street[2];
    draft.city = street[3].split(/[,\n]/)[0].trim();
  }
  const web = text.match(/\b(?:https?:\/\/)?(www\.(?!instagram|facebook|linkedin|xing|twitter|youtube|tiktok)[\w-]+(?:\.[\w-]+)+)/i);
  const domain = address.split("@")[1] ?? "";
  if (web) draft.website = `https://${web[1].toLowerCase()}`;
  else if (domain && !FREEMAIL.test(address)) draft.website = `https://${domain}`;

  const legal = [
    text.match(/(?:Amtsgericht[^\n]*?)?HRB[ \t]*\d+[ \t]*[A-Z]?\b/u)?.[0]?.trim(),
    text.match(/(?:USt\.?[- ]?Id\.?(?:[- ]?Nr\.?)?|VAT[ -]?ID)[^\n]{0,12}?[A-Z]{2}[ ]?\d[\d ]{7,12}\d/i)?.[0]?.trim(),
    text.match(/(?:Geschäftsführ\w*|GF|CEO|Vertreten durch[^:\n]*)[ \t]*[:/]?[ \t]*[A-ZÄÖÜ][\p{L}.-]+(?:[ \t]+[A-ZÄÖÜ][\p{L}.-]+)+/u)?.[0]?.trim(),
  ].filter(Boolean);
  draft.legal = legal.join(" · ");
  return draft;
};

const Field = ({ label, value, onChange, wide }: { label: string; value: string; onChange: (v: string) => void; wide?: boolean }) => (
  <label className={`flex flex-col gap-0.5 text-xs text-muted-foreground ${wide ? "sm:col-span-2" : ""}`}>
    {label}
    <input value={value} onChange={(e) => onChange(e.target.value)} className="h-8 rounded-md border border-input bg-background px-2 text-sm text-foreground" />
  </label>
);

type CompanyMatch = { id: number; name: string } | null;

export const ContactFromEmail = ({ email, onDone }: { email: EmailRecord; onDone: () => void }) => {
  const initial = useMemo(() => parseSignature(email), [email]);
  const [draft, setDraft] = useState<ContactDraft>(initial);
  const [match, setMatch] = useState<CompanyMatch>(null);
  const [companyMode, setCompanyMode] = useState<"match" | "new" | "private">("new");
  const [privateKind, setPrivateKind] = useState<"privatkunde" | "privates-umfeld" | "team-intern">("privatkunde");
  const [existingContact, setExistingContact] = useState<{ id: number; name: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [asking, setAsking] = useState(false);
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const set = (key: keyof ContactDraft) => (value: string) => setDraft((d) => ({ ...d, [key]: value }));
  const freemail = FREEMAIL.test(draft.email);

  // Vorhandene Firma (per Domain) und vorhandenen Kontakt (per Adresse) erkennen
  useEffect(() => {
    const supabase = getSupabaseClient();
    const domain = draft.email.split("@")[1];
    (async () => {
      if (domain && !freemail) {
        const { data } = await supabase.from("companies").select("id,name,website").ilike("website", `%${domain}%`).limit(5);
        const hit = (data ?? []).find((c: any) => String(c.website).toLowerCase().replace(/^https?:\/\/(www\.)?/, "").split(/[/:?#]/)[0] === domain);
        setMatch(hit ? { id: hit.id, name: hit.name } : null);
        setCompanyMode(hit ? "match" : "new");
      } else {
        setMatch(null);
        setCompanyMode("private");
      }
      if (draft.email) {
        const { data } = await supabase.from("contacts").select("id,first_name,last_name").filter("email_jsonb", "cs", JSON.stringify([{ email: draft.email }])).limit(1);
        setExistingContact(data?.[0] ? { id: data[0].id, name: [data[0].first_name, data[0].last_name].filter(Boolean).join(" ") } : null);
      }
    })();
  }, [draft.email, freemail]);

  const askClaude = async () => {
    if (!identity) return;
    setAsking(true);
    const threadId = crypto.randomUUID();
    const body = (email.body_text ?? "").slice(0, 12_000);
    try {
      await sendClaudeRequest({
        threadId,
        salesId: identity.id,
        context: { path: "/emails", mode: "extract_contact", email_id: email.id, today: new Date().toISOString().slice(0, 10) },
        prompt: `Lies aus dieser E-Mail die Kontaktdaten der Gegenseite (${draft.email}) aus – NICHT die von Katharina Kamecka / Lovely Day Flowers. Nutze die Signatur im Verlauf. Antworte ausschließlich mit einem Codeblock \`\`\`ldf-contact und darin JSON mit den Schlüsseln first_name, last_name, title, email, phone, company_name, website, address, zipcode, city, company_phone, legal (Registergericht/HRB/USt-ID/Geschäftsführung in einer Zeile). Unbekannte Werte als leerer String. Keine Vorschläge, keine Erklärung.\n\nBetreff: ${email.subject ?? ""}\nVon: ${email.from_name ?? ""} <${email.from_address ?? ""}>\nAn: ${(email.to_addresses ?? []).join(", ")}\n\n${body}`,
      });
      const supabase = getSupabaseClient();
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data } = await supabase.from("claude_requests").select("status,answer,error").eq("thread_id", threadId).maybeSingle();
        if (!data || data.status === "wartet" || data.status === "laeuft") continue;
        if (data.status !== "fertig") throw new Error(data.error ?? data.status);
        const m = String(data.answer ?? "").match(/```ldf-contact\s*([\s\S]*?)```/) ?? String(data.answer ?? "").match(/\{[\s\S]*\}/);
        const parsed = JSON.parse(m ? (m[1] ?? m[0]) : "{}");
        setDraft((d) => {
          const next = { ...d };
          for (const key of Object.keys(d) as (keyof ContactDraft)[]) {
            if (typeof parsed[key] === "string" && parsed[key].trim()) next[key] = parsed[key].trim();
          }
          return next;
        });
        notify("Claude hat die Felder ausgefüllt – bitte prüfen", { type: "success" });
        return;
      }
      throw new Error("Zeitüberschreitung");
    } catch (e) {
      notify(`Claude konnte die Mail nicht auslesen: ${e instanceof Error ? e.message : e}`, { type: "warning" });
    } finally {
      setAsking(false);
    }
  };

  const save = async () => {
    const supabase = getSupabaseClient();
    if (!/^[^\s@,()"{}]+@[^\s@,()"{}]+\.[a-z]{2,}$/i.test(draft.email.trim())) {
      notify("Bitte eine gültige E-Mail-Adresse eintragen", { type: "warning" });
      return;
    }
    setSaving(true);
    try {
      let companyId: number | null = companyMode === "match" ? match?.id ?? null : null;
      if (companyMode === "private") {
        // Personen ohne Firma bekommen eine eigene Firma je Art: in Alltagslisten ausgeblendet, per Suche auffindbar
        const person = [draft.first_name, draft.last_name].filter(Boolean).join(" ") || draft.email;
        const kindLabel = { privatkunde: "Privatkunde", "privates-umfeld": "Privates Umfeld", "team-intern": "Team / intern" }[privateKind];
        const label = privateKind === "privatkunde" ? `${person} (privat)` : person;
        const { data, error } = await supabase
          .from("companies")
          .insert({ name: label, sector: privateKind, city: draft.city || null, description: `${kindLabel}, angelegt aus E-Mail „${email.subject ?? ""}“.`, sales_id: identity?.id })
          .select("id")
          .single();
        if (error) throw error;
        companyId = data.id;
      }
      if (companyMode === "new" && draft.company_name.trim()) {
        const { data, error } = await supabase
          .from("companies")
          .insert({
            name: draft.company_name.trim(),
            website: draft.website || null,
            address: draft.address || null,
            zipcode: draft.zipcode || null,
            city: draft.city || null,
            phone_number: draft.company_phone || null,
            description: [`Angelegt aus E-Mail „${email.subject ?? ""}“.`, draft.legal && `Rechtliches: ${draft.legal}`].filter(Boolean).join("\n"),
            sales_id: identity?.id,
          })
          .select("id")
          .single();
        if (error) throw error;
        companyId = data.id;
      }

      let contactId = existingContact?.id ?? null;
      if (!contactId) {
        const { data, error } = await supabase
          .from("contacts")
          .insert({
            first_name: draft.first_name || null,
            last_name: draft.last_name || null,
            title: draft.title || null,
            company_id: companyId,
            email_jsonb: draft.email ? [{ email: draft.email, type: freemail ? "Home" : "Work" }] : [],
            phone_jsonb: draft.phone ? [{ number: draft.phone, type: "Work" }] : [],
            background: `Angelegt aus E-Mail „${email.subject ?? ""}“ (${email.sent_at ? new Date(email.sent_at).toLocaleDateString("de-DE") : ""}).`,
            first_seen: email.sent_at ?? new Date().toISOString(),
            last_seen: email.sent_at ?? new Date().toISOString(),
            sales_id: identity?.id,
          })
          .select("id")
          .single();
        if (error) throw error;
        contactId = data.id;
      }

      // Diese und alle weiteren Mails mit derselben Adresse zuordnen
      const addr = draft.email.trim().toLowerCase();
      const { error: assignError } = await supabase
        .from("emails")
        .update({ contact_id: contactId, company_id: companyId, review_status: "zugeordnet" })
        .is("contact_id", null)
        .or(`id.eq.${email.id},from_address.eq."${addr}",to_addresses.cs.{"${addr}"},cc_addresses.cs.{"${addr}"}`);
      if (assignError) throw assignError;
      notify(existingContact ? "Mail dem vorhandenen Kontakt zugeordnet" : "Kontakt angelegt und Mails zugeordnet", { type: "success" });
      onDone();
    } catch (e) {
      notify(`Fehler: ${e instanceof Error ? e.message : String(e)}`, { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-1 text-sm font-medium">
          <UserPlus className="h-4 w-4" /> Kontakt aus Mail anlegen
        </span>
        <Button type="button" size="sm" variant="outline" className="gap-1" disabled={asking} onClick={askClaude}>
          {asking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          {asking ? "Claude liest …" : "Mit Claude genauer auslesen"}
        </Button>
      </div>
      {existingContact && (
        <p className="text-xs text-green-700 dark:text-green-400">
          {existingContact.name} ist mit dieser Adresse schon angelegt – „Anlegen“ ordnet die Mail nur zu.
        </p>
      )}

      <div className="grid gap-2 sm:grid-cols-4">
        <Field label="Vorname" value={draft.first_name} onChange={set("first_name")} />
        <Field label="Nachname" value={draft.last_name} onChange={set("last_name")} />
        <Field label="Funktion" value={draft.title} onChange={set("title")} wide />
        <Field label="E-Mail" value={draft.email} onChange={set("email")} wide />
        <Field label="Telefon" value={draft.phone} onChange={set("phone")} wide />
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <Building2 className="h-3.5 w-3.5" />
          {match && (
            <label className="flex items-center gap-1">
              <input type="radio" checked={companyMode === "match"} onChange={() => setCompanyMode("match")} /> Zu vorhandener Firma: <strong>{match.name}</strong>
            </label>
          )}
          <label className="flex items-center gap-1">
            <input type="radio" checked={companyMode === "new"} onChange={() => setCompanyMode("new")} /> Neue Firma anlegen
          </label>
          <label className="flex items-center gap-1">
            <input type="radio" checked={companyMode === "private"} onChange={() => setCompanyMode("private")} /> Privatperson
          </label>
          {companyMode === "private" && (
            <select
              value={privateKind}
              onChange={(e) => setPrivateKind(e.target.value as typeof privateKind)}
              className="h-8 rounded-md border border-input bg-background px-2 text-xs"
              aria-label="Art der Privatperson"
            >
              <option value="privatkunde">Privatkunde (z. B. Brautpaar)</option>
              <option value="privates-umfeld">Privates Umfeld (Freunde, Familie)</option>
              <option value="team-intern">Team / intern</option>
            </select>
          )}
        </div>
        {companyMode === "new" && (
          <div className="grid gap-2 sm:grid-cols-4">
            <Field label="Firmenname" value={draft.company_name} onChange={set("company_name")} wide />
            <Field label="Website" value={draft.website} onChange={set("website")} wide />
            <Field label="Straße" value={draft.address} onChange={set("address")} wide />
            <Field label="PLZ" value={draft.zipcode} onChange={set("zipcode")} />
            <Field label="Ort" value={draft.city} onChange={set("city")} />
            <Field label="Telefon Zentrale" value={draft.company_phone} onChange={set("company_phone")} wide />
            <Field label="Rechtliches (HRB, USt-ID, GF)" value={draft.legal} onChange={set("legal")} wide />
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          disabled={saving || !draft.email || (companyMode === "new" && !draft.company_name.trim())}
          onClick={save}
        >
          {existingContact ? "Mail zuordnen" : "Anlegen und Mail zuordnen"}
        </Button>
        <span className="text-xs text-muted-foreground">
          Ordnet auch alle weiteren Mails mit {draft.email || "dieser Adresse"} zu. Website-Firmen werden danach automatisch angereichert.
        </span>
      </div>
    </div>
  );
};
